/**
 * <CommunicationLogDrawer> — canonical per AR-7 · D11 (FO-only visibility).
 */
import { useState } from "react";
import { toast } from "sonner";
import { OperationalDrawer, BottomActionBar } from "@/components/operational";
import { StickyNote } from "lucide-react";
import { foApi , formatApiError } from "../foApi";

const KINDS = [
  ["wake_up_call", "Wake-up call"],
  ["taxi_booked", "Taxi booked"],
  ["doctor_requested", "Doctor requested"],
  ["airport_pickup", "Airport pickup"],
  ["late_arrival", "Late arrival"],
  ["extra_pillow", "Extra pillow"],
  ["wheelchair", "Wheelchair"],
  ["misc", "Other"],
];

export default function CommunicationLogDrawer({ open, onOpenChange, guest, stay, room, onSuccess }) {
  const [kind, setKind] = useState("misc");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!subject) { toast.error("Add a subject"); return; }
    setSaving(true);
    try {
      await foApi.createComm({
        hotel_id: guest?.hotel_id || stay?.hotel_id || "default",
        kind, subject, body,
        guest_id: guest?.id, stay_id: stay?.id,
        room_id: stay?.current_room_id,
      });
      setSubject(""); setBody("");
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e, "Failed to save note"));
    } finally { setSaving(false); }
  };

  return (
    <OperationalDrawer open={open} onOpenChange={onOpenChange}
      title="Add Note (FO Only)" subtitle={guest?.display_name}
      size="sm" testid="comm-log-drawer"
      footer={
        <BottomActionBar
          primary={{ label: saving ? "Saving..." : "Save Note",
                       icon: StickyNote, tone: "primary", onClick: submit,
                       loading: saving, disabled: !subject,
                       testid: "commlog-confirm" }}
          secondaryActions={[
            { label: "Cancel", tone: "secondary", onClick: () => onOpenChange(false), testid: "commlog-cancel" }
          ]}
        />
      }>
      <div className="p-4 space-y-4">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Kind</div>
          <div className="flex flex-wrap gap-2">
            {KINDS.map(([code, label]) => (
              <button key={code}
                onClick={() => setKind(code)}
                className={[
                  "min-h-[44px] px-3 py-1.5 rounded-lg border-2 text-[13px] font-medium",
                  kind === code ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white border-slate-300 hover:border-sky-500",
                ].join(" ")}
                data-testid={`commlog-kind-${code}`}>{label}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Subject</label>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
                   className="w-full mt-1 border-2 border-slate-300 rounded-lg p-3 text-[14px] h-12"
                   placeholder="e.g. Requested cot for baby"
                   data-testid="commlog-subject"/>
        </div>
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Details</label>
          <textarea value={body} onChange={e => setBody(e.target.value)}
                      className="w-full mt-1 border-2 border-slate-300 rounded-lg p-2 text-[14px] min-h-[80px]"
                      maxLength={500}
                      data-testid="commlog-body"/>
        </div>
      </div>
    </OperationalDrawer>
  );
}
